import { Injectable, inject } from "@angular/core";
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { passwErrors } from "@interfaces/interfaces";
import { DataTables } from "@models/I_CheckDataAvailabilityDecoupling";
import { MaestroDatos } from "@mso-services/registro/maestros.service";
import { UtilsMsoService } from "@mso-services/utilsMso.service";
import { UserServiceMSO } from "@providers/UserServiceMSO";
import { Observable, Subscription, from, of } from "rxjs";

export enum CommonErrors {
  NotValid = "notValid",
  Required = "required",
}

type ErrorBirthDate = "validFormat" | "notValidDate";

const regexBirthDate = (day: number, month: number, year: number): RegExp => {
  return new RegExp(
    `(${day}|0?${day})[^\\d]*(${month}|0?${month})[^\\d]*${year}` +
      `|(${day}|0?${day})[^\\d]*${year}[^\\d]*(${month}|0?${month})` +
      `|(${month}|0?${month})[^\\d]*(${day}|0?${day})[^\\d]*${year}` +
      `|(${month}|0?${month})[^\\d]*${year}[^\\d]*(${day}|0?${day})` +
      `|${year}[^\\d]*(${month}|0?${month})[^\\d]*(${day}|0?${day})` +
      `|${year}[^\\d]*(${day}|0?${day})[^\\d]*(${month}|0?${month})`,
    "g",
  );
};

const ERROS_LITERALS: Record<passwErrors | string, string> = {
  [passwErrors.incBirthday]: "error_pass_no_date",
  [passwErrors.incName]: "v2_errorContra7",
  [passwErrors.incSurname]: "error_pass_no_surname1",
  [passwErrors.incSurname2]: "error_pass_no_surname2",
  [passwErrors.incUser]: "v2_errorContra4",
  [passwErrors.noMay]: "v2_errorContra",
  [passwErrors.noMin]: "v2_errorContra6",
  [passwErrors.noNum]: "v2_errorContra1",
  [passwErrors.tooLong]: "error_form_max20",
  [passwErrors.tooShort]: "v2_errorContra3",
  [passwErrors.requiered]: "v2_contraObl",
  [passwErrors.differentPass]: "v2_errorContra5",
};

const BAD_STATUS = "ko";

@Injectable({
  providedIn: "root",
})
export class RegistroValidatorsService {
  private vfSubscription$: Subscription;

  private userServiceMSO = inject(UserServiceMSO);
  private utilsMsoService = inject(UtilsMsoService);

  public zipCode(fcResident: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      //if esp => max cp 52999
      //if no residente => only numbers
      const resident: MaestroDatos = fcResident.value;
      const zipRegEsp = /^[0-9]{5}$/;
      const zipRegNR = /^[A-Za-z0-9]{1,15}$/;
      //
      if (control?.value?.length > 0) {
        if (resident) {
          if (resident.id === 0) {
            // residente en España
            if (zipRegEsp.test(control.value) && parseInt(control.value) < 52999) {
              return null;
            } else {
              return { validFormat: CommonErrors.NotValid };
            }
          } else {
            // No residente
            if (zipRegNR.test(control.value)) {
              return null;
            } else {
              return { validFormat: CommonErrors.NotValid };
            }
          }
        } else {
          return { validFormat: CommonErrors.NotValid };
        }
      } else {
        return null;
      }
    };
  }

  public passwd(fgPers: FormGroup, fgUsr: FormGroup): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null =>
      this.oldPasswordValidator(control.value, { ...fgPers.value, ...fgUsr.value });
  }

  private oldPasswordValidator = (value: string, register: unknown): ValidationErrors | null => {
    const control = { value: value };
    //passwordValidator
    //^(?=.*[A-Z].*[A-Z])(?=.*[!@#$&*])(?=.*[0-9].*[0-9])(?=.*[a-z].*[a-z].*[a-z]).{8}$
    const minLength = 8;
    const maxLength = 20;
    const mayReg = /[A-ZÑ]/;
    const minReg = /[a-zñ]/;
    const numReg = /\d/;

    const { birthDay, birthMonth, birthYear, name, name2, name3, user } = register as {
      birthDay: number;
      birthMonth: { id: number };
      birthYear: number;
      name: string;
      name2: string;
      name3: string;
      user: string;
    };
    let birthDate;
    if (birthYear) {
      const day = birthDay;
      const month = birthMonth.id;
      const year = birthYear.toString();
      const minYear = year.substring(2);
      birthDate = new RegExp(
        `(${day}|0?${day})[^\\d]*(${month}|0?${month})[^\\d]*${year}` +
          `|(${day}|0?${day})[^\\d]*${year}[^\\d]*(${month}|0?${month})` +
          `|(${month}|0?${month})[^\\d]*(${day}|0?${day})[^\\d]*${year}` +
          `|(${month}|0?${month})[^\\d]*${year}[^\\d]*(${day}|0?${day})` +
          `|${year}[^\\d]*(${month}|0?${month})[^\\d]*(${day}|0?${day})` +
          `|${year}[^\\d]*(${day}|0?${day})[^\\d]*(${month}|0?${month})` +
          `|(${day}|0?${day})[^\\d]*(${month}|0?${month})[^\\d]*${minYear}` +
          `|(${day}|0?${day})[^\\d]*${minYear}[^\\d]*(${month}|0?${month})` +
          `|(${month}|0?${month})[^\\d]*(${day}|0?${day})[^\\d]*${minYear}` +
          `|(${month}|0?${month})[^\\d]*${minYear}[^\\d]*(${day}|0?${day})` +
          `|${minYear}[^\\d]*(${month}|0?${month})[^\\d]*(${day}|0?${day})` +
          `|${minYear}[^\\d]*(${day}|0?${day})[^\\d]*(${month}|0?${month})`,
        "g",
      );
    }
    if (!control.value) {
      return { required: CommonErrors.Required };
    } else if (!minReg.test(control.value)) {
      return { validPassword: passwErrors.noMin };
    } else if (!mayReg.test(control.value)) {
      return { validPassword: passwErrors.noMay };
    } else if (!numReg.test(control.value)) {
      return { validPassword: passwErrors.noNum };
    } else if (control.value.length < minLength) {
      return { validPassword: passwErrors.tooShort };
    } else if (control.value.length > maxLength) {
      return { validPassword: passwErrors.tooLong };
    } else if (name && control.value.toLowerCase().indexOf(name.toLowerCase()) !== -1) {
      return { validPassword: passwErrors.incName }; //name
    } else if (name2 && control.value.toLowerCase().indexOf(name2.toLowerCase()) !== -1) {
      return { validPassword: passwErrors.incSurname }; //surname
    } else if (name3 && control.value.toLowerCase().indexOf(name3.toLowerCase()) !== -1) {
      return { validPassword: passwErrors.incSurname2 }; //surname2
    } else if (birthDate?.test(control.value.toLowerCase())) {
      return { validPassword: passwErrors.incBirthday }; //birthDay
    } else if (user && control.value.toLowerCase().indexOf(user.toLowerCase()) !== -1) {
      return { validPassword: passwErrors.incUser }; //user
    } else {
      return null;
    }
  };

  private validAge(birthDay: Date, minAge: number, maxAge: number): ErrorBirthDate | null {
    const today: Date = new Date();
    today.setHours(0);
    today.setMinutes(0);
    today.setSeconds(0);
    today.setMilliseconds(0);

    const yearsMax = new Date(today.getTime());
    yearsMax.setFullYear(today.getFullYear() - (maxAge + 1));
    const yearsMin = new Date(today.getTime());
    yearsMin.setFullYear(today.getFullYear() - minAge);

    if (birthDay <= yearsMax || birthDay > today) {
      return "notValidDate";
    }

    if (birthDay >= yearsMin) {
      return "validFormat";
    }
    return null;
  }

  public edad(fcDay: AbstractControl, fcMonth: AbstractControl, fcYear: AbstractControl): ValidatorFn {
    return (_: AbstractControl): ValidationErrors | null => {
      let validDate: boolean;
      let checkedDate = false;
      let errorValid: ErrorBirthDate | null = null;
      if (fcDay.value && fcMonth.value && fcMonth.value.id && fcYear.value) {
        //check the date
        const miDate: Date = new Date(
          parseInt(fcYear.value),
          parseInt(fcMonth.value.id) - 1,
          parseInt(fcDay.value),
        );

        if (
          miDate.getDate() === parseInt(fcDay.value) &&
          miDate.getMonth() === parseInt(fcMonth.value.id) - 1 &&
          miDate.getFullYear() === parseInt(fcYear.value)
        ) {
          errorValid = this.validAge(miDate, 18, 124);
          validDate = !errorValid;
          checkedDate = true;
        } else {
          validDate = false;
        }
      } else {
        validDate = false;
      }
      if (validDate) {
        return null;
      } else if (checkedDate) {
        return { [errorValid]: CommonErrors.NotValid };
      }
      return { notValidDate: CommonErrors.NotValid };
    };
  }

  public oldAddressValidator(control: FormControl): ValidationErrors | null {
    if (control.value) {
      const addr: string = control.value.trim();
      if (addr.length >= 2 && addr.length <= 50) {
        return null;
      } else {
        return { validFormat: CommonErrors.NotValid };
      }
    } else return null;
  }

  public supportNumberValidator(control: FormControl): ValidationErrors | null {
    if (control.value) {
      let sp: string = control.value.trim();
      sp = sp.toUpperCase();
      if (sp.length > 0) {
        if (sp.startsWith("E") || sp.startsWith("C")) {
          const splitNum = sp.split(sp[0]);
          if (splitNum.length === 2) {
            const regex = /[0-9]/;
            if (regex.test(splitNum[1])) {
              if (splitNum[1].length === 8) {
                return null;
              } else {
                return { validFormat: CommonErrors.NotValid };
              }
            } else {
              return { validFormat: CommonErrors.NotValid };
            }
          } else {
            return { validFormat: CommonErrors.NotValid };
          }
        } else {
          return { validFormat: CommonErrors.NotValid };
        }
      } else {
        return { validFormat: CommonErrors.NotValid };
      }
    } else return null;
  }

  public dniCDR(fcNacionality: AbstractControl): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const validChars = "TRWAGMYFPDXBNJZSQVHLCKET";
      const nifRexp = /^((^[A-Z]{1}[0-9]{7}[A-Z0-9]{1}$|^[T]{1}[A-Z0-9]{8}$)|^[0-9]{8}[A-Z]{1}$)/i;
      const nieRexp = /^((^[A-Z]{1}[0-9]{7}[A-Z0-9]{1}$|^[T]{1}[A-Z0-9]{8}$)|^[0-9]{8}[A-Z]{1}$)/i;

      let str: string = control.value;
      if (control.value) {
        str = str.toUpperCase();
        if (!nifRexp.test(str) && !nieRexp.test(str)) {
          return of({ validFormat: CommonErrors.NotValid });
        } else {
          let nie = str;
          const letter = str.slice(-1).toUpperCase();
          //Si la nacionalidad es distinta de español cambiamos la primera letra por un número.
          if (fcNacionality.value.id !== 71 && !parseInt(str[0])) {
            nie = str.toUpperCase().replace(/^[X]/, "0").replace(/^[Y]/, "1").replace(/^[Z]/, "2");
            const charIndex = parseInt(nie.substring(0, 8)) % 23;
            if (validChars.charAt(charIndex) === letter) {
              return this.utilsMsoService.checkDataAvailability(DataTables.NIF, control.value.toUpperCase());
            } else {
              return of({ validFormat: CommonErrors.NotValid });
            }
          } else if (fcNacionality.value.id === 71 && (!!parseInt(str[0]) || str[0] === "0")) {
            const charIndex = parseInt(nie.substring(0, 8)) % 23;
            if (validChars.charAt(charIndex) === letter) {
              return this.utilsMsoService.checkDataAvailability(DataTables.NIF, control.value.toUpperCase());
            } else {
              return of({ validFormat: CommonErrors.NotValid });
            }
          } else {
            return of({ validFormat: CommonErrors.NotValid });
          }
        }
      } else {
        //no hay dni
        return of({ required: CommonErrors.NotValid });
      }
    };
  }

  public otherDocuments(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (control.value) {
        return this.utilsMsoService.checkDataAvailability(DataTables.OTHER_DOCS, control.value.toUpperCase());
      } else {
        return of({ required: CommonErrors.NotValid });
      }
    };
  }

  public mobileCDR(fcResident: AbstractControl): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const regExpPhone = /^[6|7][0-9]{8}$/;
      const regExpPhoneNR: RegExp = /^[0-9\+\-]*$/;

      if (fcResident.value.id === 0) {
        // residente en España
        if (regExpPhone.test(control.value)) {
          return this.utilsMsoService.checkDataAvailability(DataTables.TELF, control.value);
        } else {
          return of({ validFormat: CommonErrors.NotValid });
        }
      } else {
        //no se comprueba el formato pero sí que sea único
        if (regExpPhoneNR.test(control.value)) {
          return this.utilsMsoService.checkDataAvailability(DataTables.TELF, control.value);
        } else {
          return of({ validFormat: CommonErrors.NotValid });
        }
      }
    };
  }

  public emailCDR(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const regExpEmail: RegExp = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,63})+$/;
      if (regExpEmail.test(control.value)) {
        return this.utilsMsoService.checkDataAvailability(DataTables.MAIL, control.value);
      } else {
        return of({ validFormat: CommonErrors.NotValid });
      }
    };
  }

  public userCDR(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> =>
      this.utilsMsoService.checkDataAvailability(DataTables.USER_NAME, control.value);
  }

  public promoCDR(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (control.value && !this.userServiceMSO.globalVars.FEATURES.MSO_Disable_PromoCode_Check) {
        const q = new Promise((resolve, reject) => {
          if (this.vfSubscription$) {
            this.vfSubscription$.unsubscribe();
          }
          this.vfSubscription$ = this.utilsMsoService.verifyVoucher(control.value).subscribe({
            next: (data) => {
              resolve(data);
            },
            error: (_) => {
              reject(null);
            },
          });
        });
        return from(q);
      } else {
        const q = new Promise((resolve, _) => {
          resolve(null);
        });
        return from(q);
      }
    };
  }

  public noWhitespaceValidator(control: AbstractControl) {
    const isWhitespace = (control.value || "").trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  public spaceSuffixNames() {
    return (control: AbstractControl) => {
      if (control) {
        const value = control.value;
        if (value) {
          const valueTrim = value.trim();
          if (value !== valueTrim) {
            control.setValue(valueTrim, { emitEvent: false });
            control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
          }
        }
      }
      return null;
    };
  }

  public validationPasswordFromUserData(dataUser: {
    loginUserName: string;
    name: string;
    surname?: string;
    surname2?: string;
    birthDate: { day: number; moth: number; year: number };
  }) {
    return (control: AbstractControl) => {
      let value: string = control.value;
      if (!value) {
        return { required: CommonErrors.Required };
      }
      const mayReg = /[A-ZÑ]/;
      const minReg = /[a-zñ]/;
      const numReg = /\d/;
      if (!minReg.test(value)) {
        return { invalidPassword: passwErrors.noMin };
      }
      if (!mayReg.test(value)) {
        return { invalidPassword: passwErrors.noMay };
      }
      if (!numReg.test(value)) {
        return { invalidPassword: passwErrors.noNum };
      }
      value = value.toLowerCase();

      if (value.includes(dataUser.name.toLowerCase().replace(" ", ""))) {
        return { invalidPassword: passwErrors.incName };
      }

      if (dataUser.surname && value.includes(dataUser.surname.toLowerCase().replace(" ", ""))) {
        return { invalidPassword: passwErrors.incSurname };
      }

      if (dataUser.surname2 && value.includes(dataUser.surname2.toLowerCase().replace(" ", ""))) {
        return { invalidPassword: passwErrors.incSurname2 };
      }

      if (dataUser.loginUserName && value.includes(dataUser.loginUserName.toLowerCase().replace(" ", ""))) {
        return { invalidPassword: passwErrors.incUser };
      }

      const { day, moth, year } = dataUser.birthDate;

      if (regexBirthDate(day, moth, year).test(value)) {
        return { invalidPassword: passwErrors.incBirthday };
      }

      return null;
    };
  }

  public getErrorLiteral(errorCode: passwErrors) {
    return ERROS_LITERALS[errorCode] ?? "";
  }
}
